import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

/** Email step labels (Brandon, 2026-10-30): transactional or marketing (with a topic), the reason and who decided. */
export interface StepLabel {
  kind: "transactional" | "marketing";
  topic_key?: string;
  topic_name?: string;
  reason?: string;
  by?: string;
  at?: string;
  source: "planned" | "email";
}

export interface EmailStepLabel {
  step_key: string;
  subject?: string;
  keap_email_id?: number;
  email_id?: number;
  label: StepLabel | null;
  planned?: StepLabel;
  change_with: string;
}

/** Brandon's six topics (2026-10-30), the same keys for every brand. */
export const TOPICS: { key: string; name: string }[] = [
  { key: "educational_content", name: "Educational content" },
  { key: "monthly_roundup", name: "Monthly Roundup Newsletter" },
  { key: "webinars", name: "Webinars" },
  { key: "product_promotions", name: "Product promotions" },
  { key: "service_promotions", name: "Service promotions" },
  { key: "partner_promotions", name: "Partner promotions" },
];

export function labelText(l: StepLabel | null): string {
  if (!l) return "Not decided";
  if (l.kind === "transactional") return "Transactional";
  if (!l.topic_key) return "No topic yet"; // crm-email's wording (emaillabel.Label)
  return l.topic_name || TOPICS.find((t) => t.key === l.topic_key)?.name || l.topic_key;
}

/** The automation's email steps and their labels, by step key. */
export function useEmailLabels(automationId: number) {
  return useQuery({
    queryKey: ["automation-email-labels", automationId],
    queryFn: async () => {
      const out = await api<{ steps: EmailStepLabel[] }>(`/api/v1/automations/${automationId}/email-labels`);
      return Object.fromEntries(out.steps.map((s) => [s.step_key, s])) as Record<string, EmailStepLabel>;
    },
  });
}

/** One email step's label line: the chip, who decided and why, and Change while the step has no CRM email. */
export function EmailLabelLine({ automationId, step }: { automationId: number; step: EmailStepLabel }) {
  const [open, setOpen] = useState(false);
  const l = step.label;
  const canChange = step.change_with === "automations_email_label_set";
  const tip = l ? [l.reason, l.by && `decided by ${l.by}`, l.at && formatDateTime(l.at)].filter(Boolean).join(" · ") : "Pick transactional or a marketing topic before go-live.";
  return (
    <div className="ml-9 mt-0.5 flex flex-wrap items-center gap-1.5 text-xs">
      <Badge variant={!l ? "warning" : l.kind === "transactional" ? "default" : "muted"} title={tip}>
        {labelText(l)}
      </Badge>
      {l && <span className="text-[var(--color-muted-foreground)]">{l.source === "email" ? "from the email" : "planned"}{l.reason ? `: ${l.reason}` : ""}</span>}
      {canChange ? (
        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" onClick={() => setOpen(true)}>
          Change
        </Button>
      ) : (
        <span className="text-[var(--color-muted-foreground)]">(change it on the email)</span>
      )}
      {open && <EmailLabelDialog automationId={automationId} step={step} onClose={() => setOpen(false)} />}
    </div>
  );
}

function EmailLabelDialog({ automationId, step, onClose }: { automationId: number; step: EmailStepLabel; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [kind, setKind] = useState<"transactional" | "marketing">(step.label?.kind ?? "marketing");
  const [topic, setTopic] = useState(step.label?.topic_key ?? "");
  const [reason, setReason] = useState("");
  const [decidedBy, setDecidedBy] = useState("");
  const [sure, setSure] = useState(false);
  // Switching to transactional needs a confirm (same rule as on emails): it also reaches people who unsubscribed.
  const toTransactional = kind === "transactional" && step.label?.kind !== "transactional";
  const save = useMutation({
    mutationFn: () =>
      api(`/api/v1/automations/${automationId}/email-labels`, {
        json: { step_key: step.step_key, kind, topic_key: kind === "marketing" ? topic : undefined, reason, decided_by: decidedBy || undefined, confirm: toTransactional ? true : undefined },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["automation-email-labels", automationId] });
      toast("Label saved");
      onClose();
    },
    onError: (e) => toast((e as Error).message, "error"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>Label for “{step.subject || step.step_key}”</DialogTitle>
        <DialogDescription>
          Transactional email also reaches people who unsubscribed from marketing (receipts, sign-in links, confirmations). Marketing email can have a topic people can turn off. The
          label is used when this step's email is created at go-live; earlier decisions stay in the history.
        </DialogDescription>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="lbl-kind">Kind</Label>
            <Select id="lbl-kind" value={kind} onChange={(e) => setKind(e.target.value as "transactional" | "marketing")}>
              <option value="transactional">Transactional</option>
              <option value="marketing">Marketing</option>
            </Select>
          </div>
          {kind === "marketing" && (
            <div className="space-y-1">
              <Label htmlFor="lbl-topic">Topic</Label>
              <Select id="lbl-topic" value={topic} onChange={(e) => setTopic(e.target.value)}>
                <option value="">No topic</option>
                {TOPICS.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="lbl-reason">Why</Label>
            <Textarea id="lbl-reason" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="A few words, kept with the label" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="lbl-by">Decided by (optional)</Label>
            <Input id="lbl-by" value={decidedBy} onChange={(e) => setDecidedBy(e.target.value)} placeholder="Default: you" />
          </div>
          {toTransactional && (
            <label className="flex items-start gap-2 rounded-md border border-[var(--color-warning-fg)] bg-[var(--color-warning-bg)] p-2 text-xs text-[var(--color-warning-fg)]">
              <Checkbox checked={sure} onChange={(e) => setSure(e.target.checked)} className="mt-0.5" />
              <span>Once live, this email also goes to people who unsubscribed from marketing. Brandon approved this.</span>
            </label>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !reason.trim() || (toTransactional && !sure)}>
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
